import { useMemo } from 'react'
import type { ParkingLot } from '../../map-editor-shared/store'
import { useMapData } from '../../map-editor-shared/hooks'
import { routeLayerToCategory } from '../../map-editor-shared/lib/layerUtils'
import usePermissions from '../../hooks/usePermissions'
import styles from './ParkingLotCard.module.css'

interface ParkingLotCardProps {
  lot: ParkingLot
  onEdit: (lot: ParkingLot) => void
  /** 현장 사용 표시 켜기/끄기. 없으면 토글을 숨기고 표시만 한다. */
  onToggleSiteUse?: (lot: ParkingLot) => void
  onDelete: (id: string) => void
  onSelect: (id: string) => void
  onView?: (id: string) => void
}

/** 카드에 표시할 객체 종류별 카운트. routeLayerToCategory 결과 카테고리에 매핑. */
interface ObjectCounts {
  parkingLocation: number
  cctv: number
  column: number
  emergencyBell: number
  light: number
}

const EMPTY_COUNTS: ObjectCounts = {
  parkingLocation: 0,
  cctv: 0,
  column: 0,
  emergencyBell: 0,
  light: 0,
}

/**
 * 저장된 map_data 의 모든 층 graphJson.cells 를 순회하면서 categories
 * (parkingLocation / cctv / column / emergencyBell / light) 별로 카운트.
 * cells 의 data.layer 가 비어 있으면 attrs.root.layer / data.type 도 fallback 으로 본다.
 */
function countObjects(mapDataList: unknown): ObjectCounts {
  if (!Array.isArray(mapDataList)) return EMPTY_COUNTS
  const counts: ObjectCounts = { ...EMPTY_COUNTS }
  for (const item of mapDataList) {
    const floors = (item as { map_data?: { floors?: Array<{ graphJson?: { cells?: unknown[] } }> } } | null)
      ?.map_data?.floors
    if (!Array.isArray(floors)) continue
    for (const floor of floors) {
      const cells = floor?.graphJson?.cells
      if (!Array.isArray(cells)) continue
      for (const cell of cells) {
        const c = cell as { data?: { layer?: string; type?: string }; attrs?: { root?: { layer?: string } } }
        // links 등 layer 없는 cell 은 skip
        const layer = c?.data?.layer || c?.attrs?.root?.layer || c?.data?.type
        if (!layer) continue
        const category = routeLayerToCategory(layer)
        switch (category) {
          case 'parkingLocation':
            counts.parkingLocation += 1
            break
          case 'cctv':
            counts.cctv += 1
            break
          case 'column':
            counts.column += 1
            break
          case 'emergencyBell':
            counts.emergencyBell += 1
            break
          case 'light':
          case 'occupancyLight':
            counts.light += 1
            break
        }
      }
    }
  }
  return counts
}

export default function ParkingLotCard({
  lot,
  onEdit,
  onToggleSiteUse,
  onDelete,
  onSelect,
  onView
}: ParkingLotCardProps) {
  const { canAdmin } = usePermissions()
  // server project ID 가 숫자가 아닐 경우 (순수 localStorage lot) 조회 skip.
  const numericProjectId = useMemo(() => {
    const n = Number(lot.id)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [lot.id])
  const { data: mapDataList, isLoading: isLoadingMap } = useMapData(numericProjectId)

  // 층 이름 (예: "B1, B2") - 서버 응답 순서대로 dedupe.
  const floorNames = useMemo(() => {
    if (!Array.isArray(mapDataList)) return [] as string[]
    const seen = new Set<string>()
    const out: string[] = []
    for (const item of mapDataList) {
      const floors = (item as { map_data?: { floors?: Array<{ name?: string }> } } | null)
        ?.map_data?.floors
      if (!Array.isArray(floors)) continue
      for (const f of floors) {
        const name = (f?.name || '').trim()
        if (!name || seen.has(name)) continue
        seen.add(name)
        out.push(name)
      }
    }
    return out
  }, [mapDataList])

  const counts = useMemo(() => countObjects(mapDataList), [mapDataList])
  const hasAnyCount =
    counts.parkingLocation > 0 ||
    counts.cctv > 0 ||
    counts.column > 0 ||
    counts.emergencyBell > 0 ||
    counts.light > 0

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit(lot)
  }

  const handleToggleSiteUse = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleSiteUse?.(lot)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(lot.id)
  }

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onView) onView(lot.id)
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className={`${styles.card} ${lot.inSiteUse ? styles.cardInSiteUse : ''}`} onClick={() => onSelect(lot.id)}>
      <div className={styles.cardImage}>
        {lot.thumbnail ? (
          <img src={lot.thumbnail} alt={lot.name} className={styles.thumbnail} />
        ) : (
          <div className={styles.placeholder}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 7l6-3 6 3 6-3v13l-6 3-6-3-6 3V7z" />
            </svg>
          </div>
        )}
      </div>

      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>{lot.name}</h3>
          {(onToggleSiteUse || lot.inSiteUse) && (
            <button
              type="button"
              className={`${styles.siteUseToggle} ${lot.inSiteUse ? styles.siteUseOn : ''}`}
              onClick={handleToggleSiteUse}
              disabled={!onToggleSiteUse}
              aria-pressed={!!lot.inSiteUse}
              title={lot.inSiteUse ? '현장에서 쓰는 맵 — 누르면 표시를 끕니다' : '현장에서 쓰는 맵으로 표시'}
            >
              {lot.inSiteUse ? '✓ 현장 사용 중' : '현장 사용'}
            </button>
          )}
          <span className={styles.cardDate}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {formatDate(lot.created)}
          </span>
        </div>

        <div className={styles.floorsRow}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={styles.floorsIcon}
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="3" y1="15" x2="21" y2="15" />
          </svg>
          <span className={styles.floorsLabel}>층</span>
          <span className={styles.floorsValue}>
            {numericProjectId == null
              ? '저장 전'
              : isLoadingMap && floorNames.length === 0
              ? '로딩 중...'
              : floorNames.length === 0
              ? '없음'
              : floorNames.join(', ')}
          </span>
        </div>

        {/* 객체 수량 요약 — 저장된 맵 데이터에서 카테고리별 카운트.
            데이터가 아직 없을 땐 hidden. */}
        {hasAnyCount && (
          <div className={styles.countsGrid}>
            <div className={styles.countItem}>
              <span className={styles.countLabel}>주차구역</span>
              <span className={styles.countValue}>{counts.parkingLocation}</span>
            </div>
            <div className={styles.countItem}>
              <span className={styles.countLabel}>CCTV</span>
              <span className={styles.countValue}>{counts.cctv}</span>
            </div>
            <div className={styles.countItem}>
              <span className={styles.countLabel}>기둥</span>
              <span className={styles.countValue}>{counts.column}</span>
            </div>
            <div className={styles.countItem}>
              <span className={styles.countLabel}>비상벨</span>
              <span className={styles.countValue}>{counts.emergencyBell}</span>
            </div>
            <div className={styles.countItem}>
              <span className={styles.countLabel}>조명</span>
              <span className={styles.countValue}>{counts.light}</span>
            </div>
          </div>
        )}

        {lot.description && (
          <p className={styles.cardDescription}>{lot.description}</p>
        )}

        <div className={styles.cardFooter}>
          {onView && (
            <button
              className={styles.viewButton}
              onClick={handleView}
              title="View in viewer mode"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              View
            </button>
          )}
          <button
            className={styles.editButton}
            onClick={handleEdit}
            title="Edit parking lot"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </button>
          {/* 프로젝트 삭제는 admin 전용 — backend RequireRole 매트릭스와 sync */}
          {canAdmin && (
            <button
              className={styles.deleteButton}
              onClick={handleDelete}
              title="Delete parking lot"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
